import { useCallback, useEffect, useRef } from "react";
import { useFormikContext } from "formik";
import { Flex } from "@jsq/ds/core";
import { SPACING } from "~/styles/variables";
import { type DataPackageOption } from "../hooks/useDataPackageSections";
import { useReportingQueries } from "../hooks/useReportingQueries";
import { QueryDateParametersSummary } from "./QueryDateParametersSummary";
import { QuerySelectSection } from "./QuerySelectSection";
import type { SupportedTemplateType } from "~/graphql/supergraphTypes";

type FormValues = {
  dataPackage: string;
  query: string;
};

export type QueryPreviewContext = {
  queryId: number | null;
  arenaId: number | null;
  dataPackageId: string | null;
};

type FormQuerySelectSectionProps = {
  dataPackageOptions: DataPackageOption[];
  viewerArenaId?: number | null;
  onPreviewFields: (context: QueryPreviewContext) => void;
  supportedTemplateType?: SupportedTemplateType | null;
};

const parseQueryId = (raw: string): number | null => {
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isNaN(parsed) ? null : parsed;
};

export const FormQuerySelectSection = ({
  dataPackageOptions,
  viewerArenaId,
  onPreviewFields,
  supportedTemplateType = undefined,
}: FormQuerySelectSectionProps) => {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const previousDataPackageRef = useRef(values.dataPackage);

  const { queryOptions, queriesLoading, queriesResolved, queriesArenaId } = useReportingQueries(
    {
      dataPackageId: values.dataPackage,
      dataPackageOptions,
      viewerArenaId,
      supportedTemplateType,
    },
  );

  useEffect(() => {
    const previous = previousDataPackageRef.current;
    if (previous === values.dataPackage) return;
    previousDataPackageRef.current = values.dataPackage;
    if (previous !== "") {
      void setFieldValue("query", "");
    }
  }, [values.dataPackage, setFieldValue]);

  useEffect(() => {
    if (!queriesResolved || !values.query) return;
    const isValid = queryOptions.some((option) => option.id === values.query);
    if (!isValid) {
      void setFieldValue("query", "");
    }
  }, [queryOptions, queriesResolved, setFieldValue, values.query]);

  const selectedQueryId = parseQueryId(values.query);

  const handlePreview = useCallback(() => {
    onPreviewFields({
      queryId: selectedQueryId,
      arenaId: queriesArenaId,
      dataPackageId: values.dataPackage || null,
    });
  }, [onPreviewFields, queriesArenaId, selectedQueryId, values.dataPackage]);

  if (!values.dataPackage) {
    return null;
  }

  const filteredOutByTemplateType =
    queriesResolved && queryOptions.length === 0 && supportedTemplateType != null;

  return (
    <Flex flexDirection="column" gap={SPACING.MEDIUM}>
      <QuerySelectSection
        queryOptions={queryOptions}
        queriesLoading={queriesLoading}
        onPreviewFields={handlePreview}
        showPreviewLink={Boolean(values.query)}
        filteredOutByTemplateType={filteredOutByTemplateType}
      />
      <QueryDateParametersSummary
        key={`${selectedQueryId ?? "none"}:${queriesArenaId ?? "none"}`}
        queryId={selectedQueryId}
        arenaId={queriesArenaId}
      />
    </Flex>
  );
};

export { parseQueryId };
